import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, ArrowClockwise, SpeakerHigh } from '@phosphor-icons/react';

// Animated "legal representative" avatar — animates while speaking.
const AvatarStage = ({ speaking }) => (
  <div className="relative flex items-center justify-center" data-testid="narration-avatar">
    <div className={`absolute w-40 h-40 rounded-full bg-[#002FA7]/10 ${speaking ? 'animate-ping' : ''}`} style={{ animationDuration: '1.8s' }} />
    <div className={`absolute w-32 h-32 rounded-full border-2 border-[#002FA7]/30 ${speaking ? 'animate-pulse' : ''}`} />
    <div className="relative w-28 h-28 rounded-full bg-gradient-to-b from-[#002FA7] to-[#00237D] flex items-center justify-center shadow-xl overflow-hidden">
      <svg viewBox="0 0 64 64" className="w-20 h-20 -mb-2" aria-hidden="true">
        <circle cx="32" cy="22" r="12" fill="#F4F6FF" />
        <path d="M12 60c0-12 9-20 20-20s20 8 20 20z" fill="#F4F6FF" />
        <path d="M32 40l-7 4 7 16 7-16z" fill="#002FA7" />
        <path d="M28 41l4 6 4-6-4-3z" fill="#FF9500" />
      </svg>
    </div>
    <div className="absolute -bottom-5 flex items-end gap-1 h-5">
      {[0, 1, 2, 3, 4].map(i => (
        <span key={i} className="w-1 rounded-full bg-[#002FA7]"
          style={{ height: speaking ? '100%' : '20%', animation: speaking ? `narrBar 0.8s ease-in-out ${i * 0.12}s infinite alternate` : 'none' }} />
      ))}
    </div>
    <style>{`@keyframes narrBar { from { height: 20%; opacity: .5 } to { height: 100%; opacity: 1 } }`}</style>
  </div>
);

const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Self-contained narration player: given the active narration + its audio blob URL,
// owns all playback state (play/pause/replay/seek/speed) and renders the avatar,
// transcript with the spoken segment highlighted, and key points.
export const NarrationPlayer = ({ active, audioUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const audioRef = useRef(null);

  // Reset + apply speed whenever a new audio source loads.
  useEffect(() => {
    const a = audioRef.current;
    if (a) a.playbackRate = speed;
    setPlaying(false); setProgress(0); setCurTime(0);
  }, [audioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  const togglePlay = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };
  const replay = () => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = 0; a.play(); setPlaying(true);
  };
  const onTime = () => {
    const a = audioRef.current; if (!a) return;
    setCurTime(a.currentTime);
    setProgress(a.duration ? a.currentTime / a.duration : 0);
  };
  const onSeek = (e) => {
    const a = audioRef.current; if (!a || !a.duration) return;
    const pct = Number(e.target.value);
    a.currentTime = pct * a.duration; setProgress(pct);
  };

  const segments = active?.segments || [];
  let activeSeg = -1;
  if (segments.length && progress > 0) {
    const lens = segments.map(s => (s.text || '').length || 1);
    const total = lens.reduce((a, b) => a + b, 0);
    let acc = 0; const target = progress * total;
    for (let i = 0; i < lens.length; i++) { acc += lens[i]; if (target <= acc) { activeSeg = i; break; } }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-xl p-6 flex flex-col items-center" data-testid="narration-player">
        <AvatarStage speaking={playing} />
        <div className="mt-8 text-center">
          <p className="font-outfit font-semibold text-[#09090B]">{active.title}</p>
          <p className="text-xs text-[#6B7280] font-ibm mt-0.5">v{active.version} · {active.word_count || 0} words · tone: {active.tone || 'measured'}</p>
        </div>
        <audio ref={audioRef} src={audioUrl} onTimeUpdate={onTime}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          onEnded={() => setPlaying(false)} className="hidden" data-testid="narration-audio" />
        <div className="w-full mt-5">
          <input type="range" min="0" max="1" step="0.001" value={progress} onChange={onSeek}
            className="w-full accent-[#002FA7] cursor-pointer" data-testid="narration-seek" />
          <div className="flex justify-between text-[11px] text-[#6B7280] font-ibm mt-1">
            <span>{fmtTime(curTime)}</span><span>{fmtTime(duration)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={replay} title="Replay from start" data-testid="narration-replay"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[#E5E7EB] text-[#6B7280] hover:text-[#002FA7] hover:border-[#002FA7] transition-all">
            <ArrowClockwise size={18} />
          </button>
          <button onClick={togglePlay} data-testid="narration-playpause"
            className="w-14 h-14 flex items-center justify-center rounded-full bg-[#002FA7] text-white hover:bg-[#00237D] transition-all shadow-lg">
            {playing ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
          </button>
          <div className="flex items-center gap-1">
            {[0.75, 1, 1.25].map(s => (
              <button key={s} onClick={() => setSpeed(s)} data-testid={`narration-speed-${s}`}
                className={`px-2 py-1 text-xs font-ibm font-semibold rounded ${speed === s ? 'bg-[#002FA7] text-white' : 'text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                {s}×
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-4">
        {active.key_points?.length > 0 && (
          <div className="bg-[#F4F6FF] border border-[#002FA7]/15 rounded-xl p-4" data-testid="narration-key-points">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#002FA7] font-ibm mb-2">Key Points</p>
            <ul className="space-y-1.5">
              {active.key_points.map((k, i) => (
                <li key={`kp-${i}`} className="text-sm font-ibm text-[#09090B] flex gap-2"><span className="text-[#FF9500]">•</span>{k}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="narration-transcript">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mb-3 flex items-center gap-1"><SpeakerHigh size={13} /> Transcript</p>
          <div className="space-y-3">
            {segments.map((s, i) => (
              <div key={`seg-${i}`} data-testid={`narration-segment-${i}`}
                className={`rounded-lg px-3 py-2 transition-all ${i === activeSeg ? 'bg-[#002FA7]/10 border-l-2 border-[#002FA7]' : 'border-l-2 border-transparent'}`}>
                {s.label && <p className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7]/70 font-ibm mb-0.5">{s.label}</p>}
                <p className="text-sm font-ibm text-[#09090B] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
